export interface RtaRawPoint {
  date: string;
  q: number;
  pwf: number;
}

export interface RtaPoint {
  date: string;

  /**
   * 自第一条有效记录起的生产时间
   * d
   */
  t: number;

  /**
   * 日产气量
   * 10^4 m3/d
   */
  q: number;

  /**
   * 井底流压
   * MPa
   */
  pwf: number;

  /**
   * 地层压力
   * MPa
   */
  pr: number;

  /**
   * Pr - Pwf
   * MPa
   */
  deltaP: number;

  /**
   * Pr^2 - Pwf^2
   * MPa^2
   */
  deltaP2: number;

  /**
   * q / Δp²
   */
  normalizedRate2: number;

  /**
   * Δp² / q
   */
  inverseNormalizedRate2: number;

  /**
   * 累计产气
   * 10^4 m3
   */
  gp: number;

  /**
   * 物质平衡时间
   * tmb = Gp / q
   * d
   */
  tmb: number;

  sqrtT: number;
  sqrtTmb: number;
}

export interface RtaLinearFit {
  count: number;

  slope: number;
  intercept: number;

  r2: number;
  rmse: number;

  minX: number;
  maxX: number;
}

function parseDate(
  value: string,
) {
  const time =
    new Date(value).getTime();

  return Number.isFinite(time)
    ? time
    : NaN;
}

export function buildRtaPoints(
  rawPoints: RtaRawPoint[],
  reservoirPressure: number,
): RtaPoint[] {
  if (
    !Number.isFinite(
      reservoirPressure,
    ) ||
    reservoirPressure <= 0
  ) {
    return [];
  }

  const valid =
    rawPoints
      .filter(
        (point) =>
          point.date !== '' &&
          Number.isFinite(
            point.q,
          ) &&
          Number.isFinite(
            point.pwf,
          ) &&
          point.q > 0 &&
          point.pwf > 0 &&
          point.pwf <
            reservoirPressure &&
          Number.isFinite(
            parseDate(
              point.date,
            ),
          ),
      )
      .sort(
        (a, b) =>
          parseDate(a.date) -
          parseDate(b.date),
      );

  if (
    valid.length === 0
  ) {
    return [];
  }

  const firstTime =
    parseDate(
      valid[0].date,
    );

  const temporary =
    valid.map((point) => {
      const currentTime =
        parseDate(
          point.date,
        );

      const t =
        (
          currentTime -
          firstTime
        ) /
        (
          24 *
          60 *
          60 *
          1000
        );

      return {
        ...point,
        t,
      };
    });

  let gp = 0;

  const result:
    RtaPoint[] = [];

  for (
    let i = 0;
    i <
    temporary.length;
    i += 1
  ) {
    const current =
      temporary[i];

    if (i > 0) {
      const previous =
        temporary[i - 1];

      const dt =
        current.t -
        previous.t;

      if (dt > 0) {
        /*
         * 梯形积分：
         * Gp += 0.5(qi-1 + qi)Δt
         */
        gp +=
          0.5 *
          (
            previous.q +
            current.q
          ) *
          dt;
      }
    }

    const deltaP =
      reservoirPressure -
      current.pwf;

    const deltaP2 =
      reservoirPressure **
        2 -
      current.pwf ** 2;

    if (
      deltaP <= 0 ||
      deltaP2 <= 0
    ) {
      continue;
    }

    const tmb =
      current.q > 0
        ? gp / current.q
        : NaN;

    result.push({
      date:
        current.date,

      t:
        current.t,

      q:
        current.q,

      pwf:
        current.pwf,

      pr:
        reservoirPressure,

      deltaP,

      deltaP2,

      normalizedRate2:
        current.q /
        deltaP2,

      inverseNormalizedRate2:
        deltaP2 /
        current.q,

      gp,

      tmb,

      sqrtT:
        Math.sqrt(
          Math.max(
            current.t,
            0,
          ),
        ),

      sqrtTmb:
        Math.sqrt(
          Math.max(
            tmb,
            0,
          ),
        ),
    });
  }

  return result;
}

export function fitRtaLinearTrend(
  points: RtaPoint[],
  xType:
    | 'sqrtT'
    | 'sqrtTmb',
): RtaLinearFit | null {
  const valid =
    points.filter(
      (point) =>
        Number.isFinite(
          point[xType],
        ) &&
        Number.isFinite(
          point.inverseNormalizedRate2,
        ),
    );

  if (
    valid.length < 3
  ) {
    return null;
  }

  const xs =
    valid.map(
      (point) =>
        point[xType],
    );

  const ys =
    valid.map(
      (point) =>
        point.inverseNormalizedRate2,
    );

  const meanX =
    xs.reduce(
      (sum, value) =>
        sum + value,
      0,
    ) /
    xs.length;

  const meanY =
    ys.reduce(
      (sum, value) =>
        sum + value,
      0,
    ) /
    ys.length;

  let numerator = 0;
  let denominator = 0;

  for (
    let i = 0;
    i <
    xs.length;
    i += 1
  ) {
    numerator +=
      (
        xs[i] -
        meanX
      ) *
      (
        ys[i] -
        meanY
      );

    denominator +=
      (
        xs[i] -
        meanX
      ) ** 2;
  }

  if (
    Math.abs(
      denominator,
    ) <
    1e-12
  ) {
    return null;
  }

  const slope =
    numerator /
    denominator;

  const intercept =
    meanY -
    slope * meanX;

  const predicted =
    xs.map(
      (x) =>
        intercept +
        slope * x,
    );

  const ssRes =
    ys.reduce(
      (
        sum,
        y,
        index,
      ) =>
        sum +
        (
          y -
          predicted[index]
        ) ** 2,
      0,
    );

  const ssTot =
    ys.reduce(
      (sum, y) =>
        sum +
        (
          y -
          meanY
        ) ** 2,
      0,
    );

  const r2 =
    ssTot <
    1e-12
      ? 1
      : 1 -
        ssRes / ssTot;

  const rmse =
    Math.sqrt(
      ssRes /
        ys.length,
    );

  return {
    count:
      valid.length,

    slope,
    intercept,
    r2,
    rmse,

    minX:
      Math.min(...xs),

    maxX:
      Math.max(...xs),
  };
}